import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import freightKitLogo from "../assets/logo/freightkitlogoExtended.png";

import Container from "../marketing/components/Container";
import { scrollToSection } from "../marketing/utils/scrollToSection";

const CALENDLY_URL = "https://calendly.com/shawak-vantagecybertech/30min";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuth = location.pathname === "/loginPage" || location.pathname === "/register";

  const productLinks = useMemo(
    () => [
      { label: "Product tour", section: "tour" },
      { label: "Features", section: "features" },
      { label: "Use cases", section: "use-cases" },
      { label: "Pricing", section: "pricing" },
      { label: "FAQ", section: "faq" },
    ],
    []
  );

  const goToSection = (sectionId) => {
    if (location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => scrollToSection(sectionId), 60);
      return;
    }
    scrollToSection(sectionId);
  };

  if (isAuth) return null;

  return (
    <footer className="border-t border-fk-border bg-fk-surface dark:border-white/10 dark:bg-gray-950">
      <Container className="py-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl px-2 py-1 text-sm font-semibold text-fk-ink hover:bg-fk-ink/[0.03] dark:text-gray-100 dark:hover:bg-white/5"
            >
              <span className="inline-flex h-10 w-[220px] items-center justify-start overflow-hidden rounded-xl sm:h-11 sm:w-[240px]">
                <img
                  src={freightKitLogo}
                  alt="FreightKit"
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </span>
            </Link>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-fk-muted dark:text-gray-300">
              Freight quoting software focused on speed, consistency, and margin control, built for forwarders who quote daily.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-3">
            <div>
              <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">Product</div>
              <ul className="mt-4 space-y-2 text-sm text-fk-muted dark:text-gray-300">
                {productLinks.map((l) => (
                  <li key={l.section}>
                    <button
                      type="button"
                      onClick={() => goToSection(l.section)}
                      className="hover:text-fk-ink hover:underline dark:hover:text-gray-100"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">Company</div>
              <ul className="mt-4 space-y-2 text-sm text-fk-muted dark:text-gray-300">
                <li>
                  <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="hover:text-fk-ink hover:underline dark:hover:text-gray-100">
                    Book a demo
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">Legal</div>
              <div className="mt-4 text-sm text-fk-muted dark:text-gray-300">
                For legal and data processing questions, book a demo.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-fk-border pt-8 sm:flex-row sm:items-center sm:justify-between dark:border-white/10">
          <div className="text-xs text-fk-muted dark:text-gray-400">
            © {new Date().getFullYear()} FreightKit. All rights reserved.
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
